/**
 * IntegrationsHub — landlord-facing "Integrations" page.
 *
 * Single place for a landlord to see what's connected, what can be
 * connected, and to request an integration we don't ship yet. Requests
 * go to the Auto-Improve feedback endpoint which routes them into the
 * platform-fee admin's daily digest.
 *
 * Route: /integrations (Protected requireRole="landlord")
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Building2, Camera, Calendar, Landmark, KeyRound, Wrench, ShieldCheck,
  Plug, Send, Loader2, ChevronRight, CheckCircle2, Sparkles,
} from "lucide-react";
import { api } from "@/lib/api";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

// Integrations already wired into the product.
const AVAILABLE = [
  {
    id: "stripe",
    icon: Landmark,
    name: "Stripe · Rent + payouts",
    desc: "Collect CAD rent, refund holds, split payouts to your Stripe Connect account.",
    status: "live",
    to: "/billing",
    cta: "Manage",
  },
  {
    id: "condoworks",
    icon: Building2,
    name: "CondoWorks",
    desc: "Sync bills, invoices, and vendor payments between CondoWorks and Central Rentals.",
    status: "live",
    to: "/integrations/condoworks",
    cta: "Connect",
  },
  {
    id: "butterflymx",
    icon: KeyRound,
    name: "ButterflyMX · Smart intercom",
    desc: "Grant tenants building access, sync visitor logs, and revoke keys on move-out.",
    status: "live",
    to: "/integrations/butterflymx",
    cta: "Connect",
  },
  {
    id: "matterport",
    icon: Camera,
    name: "Matterport · 3D tours",
    desc: "Attach immersive 3D tours to your listings so tenants pre-qualify themselves.",
    status: "live",
    to: "/smart-tours",
    cta: "Open",
  },
  {
    id: "mls",
    icon: Wrench,
    name: "MLS via Repliers",
    desc: "Pull comparable listings + market rent data into your dashboard.",
    status: "live",
    to: "/mls",
    cta: "Open",
  },
  {
    id: "certn",
    icon: ShieldCheck,
    name: "Certn · Tenant screening",
    desc: "Credit + ID + reference reports in 24–48 hours, PIPEDA-compliant.",
    status: "live",
    to: "/tenants/screen",
    cta: "Screen a tenant",
  },
];

// Integrations we haven't shipped yet. Clicking "Request" fires a
// feature-request into the Auto-Improve backlog so the platform-fee admin
// sees which integrations landlords actually want next.
const COMING_SOON = [
  { id: "google_calendar",  icon: Calendar,   name: "Google Calendar",       desc: "Sync viewings, inspections, and move-in dates to your team calendar." },
  { id: "quickbooks",       icon: Landmark,   name: "QuickBooks Online",     desc: "Push rent-received + expense entries into your QBO ledger automatically." },
  { id: "zapier",           icon: Plug,       name: "Zapier",                desc: "5,000+ app connectors — bridge Central Rentals with your existing tools." },
  { id: "buildium",         icon: Building2,  name: "Buildium migration",    desc: "One-click portfolio import from Buildium — owners, properties, leases." },
  { id: "yardi",            icon: Building2,  name: "Yardi export",          desc: "Nightly Yardi Voyager sync — GL codes, work orders, and rent rolls." },
  { id: "docusign",         icon: ShieldCheck, name: "DocuSign",             desc: "Escalate high-value leases to DocuSign for enterprise-grade signatures." },
];

export default function IntegrationsHub() {
  const [enabled, setEnabled] = useState({});
  const [requesting, setRequesting] = useState(null);
  const [requested, setRequested] = useState({});

  useEffect(() => { document.title = "Integrations — Central Rentals"; }, []);

  // Best-effort: query the runtime-flags endpoint to reflect any
  // per-tenant "connected" state we may write in the future. Safe to
  // fail — we just show the AVAILABLE integrations as opt-in cards.
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/auto-improve/runtime-flags`);
        if (!r.ok) return;
        const j = await r.json();
        setEnabled(j.flags || {});
      } catch { /* ignore */ }
    })();
  }, []);

  async function requestIntegration(id, name) {
    setRequesting(id);
    try {
      await api.post("/auto-improve/feedback", {
        kind: "feature",
        body: `Please add the ${name} integration for landlords.`,
        path: "/integrations",
      });
      setRequested((prev) => ({ ...prev, [id]: true }));
      toast.success(`Thanks — we'll prioritise ${name}.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send that. Try again in a moment.");
    } finally {
      setRequesting(null);
    }
  }

  return (
    <div className="max-w-5xl mx-auto px-5 md:px-8 py-10" data-testid="integrations-hub">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center">
          <Plug className="w-5 h-5 text-emerald-700"/>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-700">Landlord tools</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tighter text-slate-900 leading-tight mt-1">Integrations</h1>
          <p className="mt-2 text-slate-500 text-sm max-w-2xl">
            Plug Central Rentals into the other apps you already run — payments, screening, smart building tech, and the calendar you actually check.
          </p>
        </div>
      </div>

      {/* Available */}
      <section className="mt-10" data-testid="integrations-available">
        <h2 className="font-display text-lg font-bold tracking-tight text-slate-900">Available now</h2>
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          {AVAILABLE.map((it) => {
            const Icon = it.icon;
            const isEnabled = enabled[`integration:${it.id}`]?.value === true;
            return (
              <div key={it.id} data-testid={`integration-${it.id}`}
                className="rounded-2xl border border-slate-200 bg-white p-4 flex items-start gap-3 hover:border-emerald-300 hover:shadow-sm transition">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center flex-none">
                  <Icon className="w-5 h-5"/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-semibold text-sm text-slate-900 truncate">{it.name}</div>
                    {isEnabled && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                        <CheckCircle2 className="w-3 h-3"/> Connected
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-slate-500 leading-relaxed">{it.desc}</p>
                  <Link to={it.to} data-testid={`integration-cta-${it.id}`}
                    className="mt-3 inline-flex items-center gap-1 text-[12px] font-bold text-emerald-700 hover:text-emerald-800">
                    {it.cta} <ChevronRight className="w-3.5 h-3.5"/>
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Coming soon — request driven */}
      <section className="mt-10" data-testid="integrations-coming-soon">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-bold tracking-tight text-slate-900">Coming soon — vote it up</h2>
          <span className="text-xs text-slate-500 inline-flex items-center gap-1"><Sparkles className="w-3.5 h-3.5 text-emerald-500"/> The most-requested ones ship first.</span>
        </div>
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          {COMING_SOON.map((it) => {
            const Icon = it.icon;
            const isRequested = !!requested[it.id];
            return (
              <div key={it.id} data-testid={`coming-soon-${it.id}`}
                className="rounded-2xl border border-dashed border-slate-300 bg-slate-50/50 p-4 flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-white text-slate-600 border border-slate-200 flex items-center justify-center flex-none">
                  <Icon className="w-5 h-5"/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-slate-900 truncate">{it.name}</div>
                  <p className="mt-1 text-xs text-slate-500 leading-relaxed">{it.desc}</p>
                  <button
                    onClick={() => requestIntegration(it.id, it.name)}
                    disabled={requesting === it.id || isRequested}
                    data-testid={`coming-soon-request-${it.id}`}
                    className={`mt-3 inline-flex items-center gap-1.5 text-[12px] font-bold px-3 py-1.5 rounded-lg transition-colors ${
                      isRequested
                        ? "bg-emerald-50 text-emerald-700 border border-emerald-200 cursor-default"
                        : "bg-slate-900 hover:bg-slate-800 text-white"
                    }`}
                  >
                    {requesting === it.id ? (
                      <><Loader2 className="w-3.5 h-3.5 animate-spin"/> Sending…</>
                    ) : isRequested ? (
                      <><CheckCircle2 className="w-3.5 h-3.5"/> Requested</>
                    ) : (
                      <><Send className="w-3.5 h-3.5"/> Request this</>
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Custom request footnote */}
      <section className="mt-10 rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 via-white to-emerald-50/40 p-6" data-testid="integrations-custom">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center flex-none">
            <Plug className="w-5 h-5"/>
          </div>
          <div>
            <div className="font-display text-base font-bold text-slate-900">Need something not listed?</div>
            <p className="mt-1 text-sm text-slate-600 max-w-2xl">
              Open the &ldquo;Quick question&rdquo; tab on the right edge of any page and send us the app you want to connect. Requests routed there hit our nightly backlog and are triaged against every other landlord ask — the popular ones ship first.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
